import { useMemo } from 'react';

import type { MenuItem } from 'primereact/menuitem';
import { useDispatch } from 'react-redux';

import { addToast } from '@/app/slices/toast';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import {
  runWorkflow,
  WORKFLOW,
  type Workflow as WorkflowType,
  type WorkflowValidation,
} from '@/utils/workflow';

export function useWorkflowMenu(
  environmentId: number,
  workflows: WorkflowType[],
  ids: string | string[],
  onWorkflowExecute?: () => void,
  workflowValidation?: WorkflowValidation,
): MenuItem[] {
  const dispatch = useDispatch();
  const confirm = useConfirm();
  const confirmWorkflows = useFeatureFlag('shipping_confirm_workflows');

  const recordIds = useMemo(() => {
    if (Array.isArray(ids)) {
      return ids.map((id) => ({ RecordId: id }));
    }
    return [{ RecordId: ids }];
  }, [ids]);

  return useMemo(
    () =>
      workflows.map<MenuItem>((workflow: WorkflowType) => ({
        label: workflow.label,
        icon: 'pi pi-play-circle',
        command: async () => {
          if (workflowValidation) {
            const validation = await workflowValidation(workflow);
            if (!validation.isValid) {
              dispatch(
                addToast({
                  severity: 'error',
                  summary: 'Workflow Restriction',
                  detail:
                    validation.message || 'This workflow cannot be executed.',
                  life: ToastLife.LONG,
                }),
              );
              return;
            }
          }

          const executeWorkflow = async () => {
            try {
              await runWorkflow(environmentId, workflow.id, recordIds);
              dispatch(
                addToast({
                  severity: 'success',
                  summary: 'Workflow Executed',
                  detail: `${workflow.label} workflow completed successfully.`,
                  life: ToastLife.DEFAULT,
                }),
              );
            } catch (error) {
              const errorMessage =
                error instanceof Error ? error.message : String(error);
              dispatch(
                addToast({
                  severity: 'error',
                  summary: 'Workflow Error',
                  detail: `Failed to execute ${workflow.label} workflow. Please try again. Error: ${errorMessage}`,
                  life: ToastLife.LONG,
                }),
              );
              console.error('Workflow error:', error);
            } finally {
              if (onWorkflowExecute) {
                onWorkflowExecute();
              }
            }
          };

          if (
            confirmWorkflows &&
            workflow.entityName === WORKFLOW.Ship.entityName
          ) {
            const count = recordIds.length;
            confirm({
              title: 'Confirm Workflow',
              description: (
                <>
                  Run <b>{workflow.label}</b> workflow for {count} shipment
                  {count === 1 ? '' : 's'}?
                </>
              ),
              confirmLabel: 'OK',
              cancelLabel: 'Cancel',
              onConfirm: executeWorkflow,
            });
          } else {
            await executeWorkflow();
          }
        },
      })),
    [
      workflows,
      workflowValidation,
      dispatch,
      environmentId,
      recordIds,
      onWorkflowExecute,
      confirm,
      confirmWorkflows,
    ],
  );
}
